import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Product } from '../../products/products.model';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from 'rxjs';
import { ProductsService } from '../../products/products.service';
import { DerivedOfferStatus, Offer } from '../offers.model';
import { OffersService } from '../offers.service';
import { ToastrService } from 'ngx-toastr';
import { currentEanOf } from '../../../core/utils/current-ean.util';


@Component({
  selector: 'app-offers-modal',
  // CommonModule gives us the `number` (DecimalPipe), `currency`, and
  // other built-in pipes used by the dropdown templates (CP/SP/MRP
  // formatting). Without it Angular's strict template checker errors
  // with NG8004: "No pipe found with name 'number'" at build time.
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './offers-modal.component.html',
  styleUrl: './offers-modal.component.scss'
})
export class OffersModalComponent implements OnInit {
  @Input() data!: Offer;
  offerForm: FormGroup;
  private fb = inject(FormBuilder);
  products: Product[] = [];
  productInput$ = new Subject<string>();
  loading = false;
  /** True while the create/update HTTP request is in flight. Disables the
   *  Save button + shows a "Saving…" label so the cashier doesn't double-
   *  click and create two offers (or two PATCH calls). */
  saving = false;
  private productService = inject(ProductsService);
  private offersService = inject(OffersService);
  private toastr = inject(ToastrService);
  /** Bridge to the NgbModal instance — present ONLY when this component
   *  is opened from offers.component via modalService.open() (the Edit
   *  flow). The /create-offer route renders this same component as a
   *  full page, where there's no NgbModal in scope. Optional injection
   *  lets the component support both contexts; isModal() picks the
   *  right close/navigate behavior per case. */
  activeModal = inject(NgbActiveModal, { optional: true });
  private router = inject(Router);
  variantList: any[] = [];
  stores: { id: string; name?: string; storeCode?: string }[] = [];

  /** True when running inside an NgbModal (Edit flow); false on the
   *  standalone /create-offer page. Drives whether the close X / Cancel
   *  buttons dismiss the modal or route back to the offers list. */
  get isModal(): boolean {
    return !!this.activeModal;
  }

  /** Drives the modal header label: "Edit Offer" when editing an existing
   *  row, "Add Offer" when creating new. Detected via the presence of an
   *  id on the @Input data. */
  get isEditing(): boolean {
    return !!this.data?.id;
  }

  // ── Read-only metadata helpers ─────────────────────────────────────
  // Used by the Metadata section at the top of the edit modal. All of
  // these return a display-ready string or null — templates render the
  // null case as an em dash so blank fields are visibly intentional
  // rather than looking like a layout bug.

  /** Pretty name for the user who created this offer. Backend may
   *  return createdBy as a userId string OR a populated user object
   *  (depends on the API's include policy); we handle both. Falls back
   *  to createdByUser (a Prisma-style relation name some endpoints use). */
  get createdByLabel(): string | null {
    const cb: any = (this.data as any)?.createdBy ?? (this.data as any)?.createdByUser;
    if (!cb) return null;
    if (typeof cb === 'string') return cb; // raw userId — better than nothing
    return cb.name || cb.email || cb.id || null;
  }

  /** Pretty name for the user who last touched this offer. Same shape
   *  rules as createdByLabel — string, populated object, or absent. */
  get updatedByLabel(): string | null {
    const ub: any = (this.data as any)?.updatedBy;
    if (!ub) return null;
    if (typeof ub === 'string') return ub;
    return ub.name || ub.email || ub.id || null;
  }

  /** Same derivation as offers.component.derivedStatus — duplicated
   *  rather than imported to keep the modal independent of the parent
   *  list page (the modal can be opened from /create-offer too).
   *  EXPIRED beats PAUSED beats SCHEDULED beats ACTIVE. */
  get derivedStatus(): DerivedOfferStatus | null {
    if (!this.data?.id) return null;
    const today = this.istDayNum(new Date());
    const start = this.data.startDate ? this.istDayNum(new Date(this.data.startDate)) : NaN;
    const end = this.data.endDate ? this.istDayNum(new Date(this.data.endDate)) : NaN;
    if (!isNaN(end) && end < today) return 'EXPIRED';
    if (!isNaN(start) && start > today) return 'SCHEDULED';
    return this.data.isActive ? 'ACTIVE' : 'PAUSED';
  }

  /** IST calendar-day number (YYYYMMDD). Offers store start/end as the
   *  UTC-midnight of a calendar day, so a raw timestamp comparison expires
   *  an offer ending "today" from 05:30 IST. Comparing IST day numbers keeps
   *  it valid for the whole IST day. Mirrors offers.component.istDayNum. */
  private istDayNum(d: Date): number {
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const ist = new Date(d.getTime() + IST_OFFSET_MS);
    return ist.getUTCFullYear() * 10000 + (ist.getUTCMonth() + 1) * 100 + ist.getUTCDate();
  }

  /** Compact human summary of the reward — e.g. "₹50 off" or "10%" or
   *  "Buy 2 get 1 free". Templates show this in the metadata strip so
   *  admins don't have to scroll into the Rule section just to remind
   *  themselves what the offer actually gives. Returns null when no
   *  rule field is populated yet (a brand-new draft). */
  get rewardSummary(): string | null {
    const d: any = this.data || {};
    const pct = d.percentOff ? Number(d.percentOff) : null;
    const amt = d.priceOffAmount ? Number(d.priceOffAmount) : null;
    const buy = d.buyQuantity ? Number(d.buyQuantity) : null;
    const free = d.freeQuantity ? Number(d.freeQuantity) : null;
    if (pct) return `${pct}% off`;
    if (amt) return `₹${amt} off`;
    if (buy && free) return `Buy ${buy} get ${free} free`;
    return null;
  }

  /** Short text describing where the offer is scoped: "All stores",
   *  "N stores", or a single store name when only one is selected.
   *  Reads from the same backend payload the modal already uses to
   *  hydrate the store picker, so it's always in sync. */
  get scopeSummary(): string {
    const stores: any[] = (this.data as any)?.offerStores ?? (this.data as any)?.storeIds ?? [];
    if (!stores.length) return 'All stores';
    if (stores.length === 1) {
      const s = stores[0];
      return s?.store?.name || s?.name || '1 store';
    }
    return `${stores.length} stores`;
  }

  // Offer types that use the multi-variant freeItems[] junction (CHOICE or SET).
  // Anything in this set surfaces the freeItems picker instead of the legacy
  // single freeItemVariantId picker.
  private readonly multiFreeOfferTypes = new Set([
    'BillRangeGetFreeItem',
    'BillRangeGetFreeSet',
    // Bundle rewards: the GET pool of a "buy the bundle" offer.
    'BuyXYGetZFree',
    'BuyXYGetZSpecialPrice',
  ]);

  /** One-line explanation of how the POS applies each type. */
  readonly typeHints: Record<string, string> = {
    PriceOffOnProduct: 'Flat ₹ off the pack MRP of the picked products (or per-SKU prices via the Offers Sheet upload). MRP − amount is the till price even when the normal selling price is lower.',
    PercentOffOnMRP: '% off the pack MRP on the picked products. MRP − % is the till price even when the normal selling price is lower.',
    BuyXGetYFree: 'Buy X units from the picked products, get Y more free — the cheapest units are the free ones. Set a percent or amount instead to give Y at a discount (e.g. 3rd at 50% off).',
    BuyXYGetZSpecialPrice: 'Special Price = the price of the whole bundle (one of each picked product per set). Set Buy Qty for "any N of these for ₹X". Add Free Items to sell THOSE at the special price when the bundle is bought.',
    BuyXYGetZFree: 'When every picked product is on the bill (Qty per set = 1 each), the free items are added at ₹0.',
    BuyXYGetDiscountOnX: 'When every picked product is on the bill, each of their units gets this % off.',
    BuyXYGetAmountDiscountOnX: 'When every picked product is on the bill, each of their units gets this ₹ off.',
    BillRangeGetPercentOff: '% off the bill once the subtotal (after item offers) is inside the range. One bill offer applies per bill.',
    BillRangeGetAmountOff: 'Flat ₹ off the bill once the subtotal is inside the range.',
    BillRangeGetFreeItem: 'Customer picks N free items from the list once the subtotal is inside the range.',
    BillRangeGetFreeSet: 'All listed items are added free once the subtotal is inside the range.',
    BillRangeGetScratchCard: 'Receipt is tagged so the customer collects a scratch card. No money off.',
    CategoryPercentDiscount: '% off the selling price of every product in the picked categories.',
    DepartmentPercentDiscount: '% off the selling price of every product in the picked departments.',
    BrandPercentDiscount: '% off the selling price of every product of the picked brands.',
    AllProductsDiscount: '% or ₹ off every product on the bill (add exclusions below if needed).',
    HappyHourDiscount: '% or ₹ off between the daily start and end times (store clock). Pick products to limit it, else store-wide.',
    ProgressiveDiscount: 'Buy more, save more: the slab the line quantity reaches sets the discount. Pick products to limit it, else store-wide.',
    ExclusionBasedDiscount: '% or ₹ off every product except the excluded ones.',
  };

  get typeHint(): string {
    return this.typeHints[this.offerForm.get('offerType')?.value] ?? '';
  }

  /** Field label overrides per type (the special price reuses the amount column). */
  get amountLabel(): string {
    const t = this.offerForm.get('offerType')?.value;
    if (t === 'BuyXYGetZSpecialPrice') return 'Special Price';
    if (t === 'BuyXGetYFree') return 'Amount Off per Y unit';
    if (t === 'BuyXYGetAmountDiscountOnX') return 'Amount Off per unit';
    return 'Discount Amount';
  }

  get percentLabel(): string {
    const t = this.offerForm.get('offerType')?.value;
    if (t === 'BuyXGetYFree') return 'Percent Off per Y unit';
    if (t === 'PercentOffOnMRP') return 'Percent Off MRP';
    return 'Discount Percent';
  }

  /** Which visible fields are mandatory for the chosen type (drives the asterisks). */
  readonly requiredFieldsMap: Record<string, string[]> = {
    PriceOffOnProduct: ['priceOffAmount', 'variantIds'],
    PercentOffOnMRP: ['percentOff', 'variantIds'],
    BuyXGetYFree: ['buyQuantity', 'freeQuantity', 'variantIds'],
    BuyXYGetZSpecialPrice: ['priceOffAmount', 'variantIds'],
    BuyXYGetZFree: ['freeItems', 'variantIds'],
    BuyXYGetDiscountOnX: ['percentOff', 'variantIds'],
    BuyXYGetAmountDiscountOnX: ['priceOffAmount', 'variantIds'],
    BillRangeGetPercentOff: ['billAmountMin', 'percentOff'],
    BillRangeGetAmountOff: ['billAmountMin', 'priceOffAmount'],
    BillRangeGetFreeItem: ['billAmountMin', 'freeItems'],
    BillRangeGetFreeSet: ['billAmountMin', 'freeItems'],
    BillRangeGetScratchCard: ['billAmountMin'],
    CategoryPercentDiscount: ['percentOff', 'categoryIds'],
    DepartmentPercentDiscount: ['percentOff', 'departmentIds'],
    BrandPercentDiscount: ['percentOff', 'brandIds'],
    AllProductsDiscount: [],
    HappyHourDiscount: ['startTime', 'endTime'],
    ProgressiveDiscount: ['progressiveDiscount'],
    ExclusionBasedDiscount: ['exclusionVariantIds'],
  };

  isRequired(field: string): boolean {
    const t = this.offerForm.get('offerType')?.value;
    return (this.requiredFieldsMap[t] ?? []).includes(field);
  }

  // ── Progressive discount slabs ──────────────────────────────────────
  get slabsArray(): FormArray {
    return this.offerForm.get('slabs') as FormArray;
  }

  private makeSlabRow(minQty: number | null = null, percentOff: number | null = null, priceOffAmount: number | null = null): FormGroup {
    return this.fb.group({
      minQty: [minQty, [Validators.required, Validators.min(1)]],
      percentOff: [percentOff],
      priceOffAmount: [priceOffAmount],
    });
  }

  addSlabRow() {
    this.slabsArray.push(this.makeSlabRow());
  }

  removeSlabRow(idx: number) {
    this.slabsArray.removeAt(idx);
  }

  private hydrateSlabs(raw: unknown) {
    this.slabsArray.clear();
    let v: any = raw;
    if (typeof v === 'string') {
      try { v = JSON.parse(v); } catch { v = null; }
    }
    if (v && !Array.isArray(v) && Array.isArray(v.slabs)) v = v.slabs;
    const list: any[] = Array.isArray(v)
      ? v
      : v && typeof v === 'object'
        ? Object.entries(v).map(([k, val]) => ({ minQty: k, percentOff: val }))
        : [];
    for (const s of list) {
      const minQty = Number(s?.minQty ?? s?.qty ?? s?.min);
      const pct = Number(s?.percentOff ?? s?.percent ?? s?.pct);
      const amt = Number(s?.priceOffAmount ?? s?.amount ?? s?.amountOff);
      if (!(minQty > 0)) continue;
      this.slabsArray.push(this.makeSlabRow(minQty, pct > 0 ? pct : null, amt > 0 ? amt : null));
    }
  }

  /** ISO instant (or "HH:mm") from the API → "HH:mm" on the IST clock for the time input. */
  private toTimeInput(v: unknown): string {
    if (v == null || v === '') return '';
    const s = String(v);
    const hm = /^(\d{1,2}):(\d{2})/.exec(s);
    if (hm && !/[T ]/.test(s)) return `${hm[1].padStart(2, '0')}:${hm[2]}`;
    const d = new Date(s);
    if (isNaN(d.getTime())) return '';
    const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    return `${String(ist.getUTCHours()).padStart(2, '0')}:${String(ist.getUTCMinutes()).padStart(2, '0')}`;
  }

  offerTypes = [
    { label: 'Price Off On Product', value: 'PriceOffOnProduct' },
    { label: 'Percent Off On MRP', value: 'PercentOffOnMRP' },
    { label: 'Buy X Get Y Free', value: 'BuyXGetYFree' },
    { label: 'Buy XY Get Z Special Price', value: 'BuyXYGetZSpecialPrice' },
    { label: 'Buy XY Get Z Free', value: 'BuyXYGetZFree' },
    { label: 'Buy XY — Item % Discount', value: 'BuyXYGetDiscountOnX' },
    { label: 'Buy XY — Item Amount Off', value: 'BuyXYGetAmountDiscountOnX' },
    { label: 'Bill Range — Percent Off', value: 'BillRangeGetPercentOff' },
    { label: 'Bill Range — Amount Off', value: 'BillRangeGetAmountOff' },
    { label: 'Bill Range — Free Item (X OR Y)', value: 'BillRangeGetFreeItem' },
    { label: 'Bill Range — Free Set (X AND Y)', value: 'BillRangeGetFreeSet' },
    { label: 'Bill Range Get Scratch Card', value: 'BillRangeGetScratchCard' },
    { label: 'Category Percent Discount', value: 'CategoryPercentDiscount' },
    { label: 'Department Percent Discount', value: 'DepartmentPercentDiscount' },
    { label: 'Brand Percent Discount', value: 'BrandPercentDiscount' },
    { label: 'All Products Discount', value: 'AllProductsDiscount' },
    { label: 'Happy Hour Discount', value: 'HappyHourDiscount' },
    { label: 'Progressive Discount', value: 'ProgressiveDiscount' },
    { label: 'Exclusion Based Discount', value: 'ExclusionBasedDiscount' }
  ];

  constructor() {
    this.offerForm = this.fb.group({
      offerType: [''],
      offerName: [''],
      description: [''],
      startDate: [''],
      endDate: [''],
      startTime: [''],
      endTime: [''],
      priceOffAmount: [0],
      percentOff: [0],
      billAmountMin: [0],
      billAmountMax: [0],
      buyQuantity: [0],
      freeQuantity: [0],
      priority: [0],
      combinable: [true],
      // Progressive discount tiers; serialised to progressiveDiscount on save.
      slabs: this.fb.array([]),
      appliesToAllProducts: [true],
      appliesToAllBrands: [true],
      appliesToAllCategories: [true],
      appliesToAllDepartments: [true],
      freeItemVariantId: [null],
      // Multi-row free-items table. Each row: { variantId, quantity, role }.
      // role is automatically forced to CHOICE for BillRangeGetFreeItem and
      // SET for BillRangeGetFreeSet just before save — admins don't pick it.
      freeItems: this.fb.array([]),
      // CHOICE_N: how many items the cashier picks from the list. Default
      // 1 = single-pick (legacy behavior). Set higher to enable multi-
      // select picker at the POS. Ignored for SET offers.
      freeChoiceCount: [1],
      variantIds: [[]],
      categoryIds: [[]],
      departmentIds: [[]],
      brandIds: [[]],
      // Empty array = "applies to all stores" (matches backend semantics:
      // OfferStore.none() means no scoping). Multi-select fills it.
      storeIds: [[]],
      exclusionVariantIds: [[]],
      isActive: [true]
    });

    this.setupProductSearch();
  }

  ngOnInit(): void {
    this.loadStores();
    if (this.data && this.data.endDate) {
      // 1) Immediate hydrate from the list row passed in by the parent.
      //    This renders the form instantly (no blank flash) using whatever
      //    the list endpoint returned — variantIds populate, but variant
      //    display labels may be stubs if the list isn't enriched.
      this.applyOfferData(this.data);

      // 2) Detail fetch: pull the fully-enriched offer (variants/exclusions/
      //    freeItems with product names + pricing) and re-hydrate. This is
      //    the authoritative source — it upgrades "(saved)" stubs to real
      //    product names and guarantees the pickers reflect the DB exactly,
      //    regardless of how stale/partial the in-memory list row was.
      if (this.data.id) {
        this.offersService.getOffer(this.data.id).subscribe({
          next: (fresh) => {
            if (!fresh) return;
            // Preserve the date-only formatting + re-run all hydration.
            this.data = fresh;
            this.applyOfferData(fresh);
          },
          // On error keep the list-row hydration from step 1 — better a
          // partial form than a broken one. Admin can still search/edit.
          error: () => {},
        });
      }
    }
  }

  /**
   * Patch scalar fields + hydrate the relation-backed controls (freeItems
   * FormArray, variantIds, storeIds) from an offer payload. Safe to call
   * more than once — re-running with fresher data upgrades the pickers.
   */
  /**
   * Per-SKU price lines of an offer created by the MRP-off-list upload
   * (OfferVariant.offerPrice set). Read-only here: prices are fixed by the
   * sheet; the picker above still controls WHICH variants stay on the offer
   * (the backend carries each remaining line's price over on save).
   */
  priceLines: Array<{
    sku: string;
    name: string;
    mrp: number | null;
    offerPrice: number | null;
    percentOff: number | null;
    priceOffAmount: number | null;
  }> = [];

  private applyOfferData(data: Offer) {
    data.endDate = this.getDateOnly(data.endDate);
    data.startDate = this.getDateOnly(data.startDate);
    this.priceLines = ((data as any).offerVariants || [])
      .filter((v: any) => v?.offerPrice != null || v?.percentOff != null || v?.priceOffAmount != null)
      .map((v: any) => {
        const product = v.variant?.product || {};
        return {
          sku: product.sku ?? '',
          name: product.longDescription || product.name || v.variant?.variantName || '',
          mrp: v.mrp != null ? parseFloat(v.mrp) : null,
          offerPrice: v.offerPrice != null ? parseFloat(v.offerPrice) : null,
          percentOff: v.percentOff != null ? parseFloat(v.percentOff) : null,
          priceOffAmount: v.priceOffAmount != null ? parseFloat(v.priceOffAmount) : null,
        };
      });
    // patchValue ignores keys it doesn't know about, so freeItems FormArray
    // and variantIds / storeIds must be hydrated separately from the API
    // payload (which uses offerVariants / offerStores, not the form key names).
    const { progressiveDiscount, ...scalars } = data as any;
    this.offerForm.patchValue({
      ...scalars,
      startTime: this.toTimeInput((data as any).startTime),
      endTime: this.toTimeInput((data as any).endTime),
    });
    this.hydrateSlabs(progressiveDiscount);
    // A bundle saved before the multi-item reward existed carries its gift
    // in the legacy single column; show it as a SET row so it is kept.
    const freeRows: any[] = (data as any).freeItems || [];
    const legacyGift = (data as any).freeItemVariantId;
    const useMulti = this.multiFreeOfferTypes.has((data as any).offerType);
    this.hydrateFreeItems(
      useMulti && !freeRows.length && legacyGift
        ? [{ variantId: legacyGift, quantity: (data as any).freeQuantity || 1, role: 'SET' }]
        : freeRows,
    );
    this.hydrateVariantIds((data as any).offerVariants || []);
    this.hydrateStoreIds((data as any).offerStores || (data as any).storeIds || []);
  }

  get freeItemsArray(): FormArray {
    return this.offerForm.get('freeItems') as FormArray;
  }

  private hydrateFreeItems(items: Array<any>) {
    this.freeItemsArray.clear();
    // Pre-seed variantList with the saved variants so ng-select has
    // something to render against the variantId form value. Without
    // this, the dropdown shows empty labels on edit — admins assume
    // the freeItems are blank and accidentally save them away.
    const seedRows: any[] = [];
    for (const it of items) {
      this.freeItemsArray.push(this.makeFreeItemRow(it.variantId, it.quantity ?? 1));

      // it.variant is enriched by the backend's findAll include block.
      // Defensive: if backend response is old (no enrichment yet), the
      // ng-select stays blank until admin types — preserves prior
      // behavior rather than crashing.
      if (it.variant) {
        const v = it.variant;
        const product = v.product || {};
        const productLongDesc = product.longDescription || product.name || '';
        const variantName = v.variantName || '';
        const latestPricing = Array.isArray(v.pricing) && v.pricing.length > 0 ? v.pricing[0] : null;
        const barcode = currentEanOf(v);
        const displayLabel = [productLongDesc, variantName, product.sku, barcode]
          .filter((x: any) => x && String(x).trim().length > 0)
          .join(' · ');
        seedRows.push({
          ...v,
          barcode,
          id: v.id,
          variantName: variantName || productLongDesc || '(unnamed)',
          displayLabel,
          longDescription: productLongDesc,
          productSku: product.sku,
          productName: product.name,
          mrp: latestPricing ? parseFloat(latestPricing.mrp) : null,
          costPrice: latestPricing ? parseFloat(latestPricing.costPrice) : null,
          sellingPrice: latestPricing ? parseFloat(latestPricing.sellingPrice) : null,
        });
      }
    }
    // Merge seed rows into variantList by id (dedup) so subsequent
    // typeahead searches still work, but the initial dropdown can
    // render saved values.
    if (seedRows.length > 0) {
      // Upsert by id: a freshly-fetched enriched row must OVERRIDE any
      // earlier "(saved)" stub for the same variant (which the immediate
      // list-row seed may have added). Skip-if-exists would let the stub
      // win and the pill would never upgrade to the real product name.
      const seedIds = new Set(seedRows.map((r) => r.id));
      const preserved = this.variantList.filter((v) => !seedIds.has(v.id));
      this.variantList = [...preserved, ...seedRows];
    }
  }

  private hydrateStoreIds(input: any[]) {
    // findAll returns offerStores as [{ storeId }, ...]; create/update sends storeIds[].
    const ids = input
      .map((s) => (typeof s === 'string' ? s : s?.storeId))
      .filter((x) => !!x);
    if (ids.length) {
      this.offerForm.patchValue({ storeIds: ids });
    }
  }

  /**
   * Populate the variantIds form control and seed the ng-select variantList
   * from the API's offerVariants array (shape: [{variantId, variant: {...}}]).
   *
   * Without this, opening an existing offer for edit always shows an empty
   * Applicable Variants picker — and clicking "Update Offer" would then send
   * variantIds:[] which wipes the OfferVariant rows via replaceJunction.
   *
   * Mirrors the same pattern used by hydrateFreeItems() / hydrateStoreIds().
   */
  private hydrateVariantIds(items: Array<any>) {
    const ids = items.map((it) => it.variantId).filter(Boolean);

    // Build display rows for the picker.
    //   1. Backend returns enriched variant data (it.variant present) →
    //      build a rich row with name / SKU / pricing so the pill shows
    //      the product name.
    //   2. Backend returns only variantId (not enriched) → add a minimal
    //      placeholder so ng-select can still render a pill (prevents the
    //      "blank selected value" confusion that leads admins to think no
    //      variants are saved and accidentally wipe them).
    const seedRows: any[] = [];
    for (const it of items) {
      if (!it.variantId) continue;
      if (it.variant) {
        const v = it.variant;
        const product = v.product || {};
        const productLongDesc = product.longDescription || product.name || '';
        const variantName = v.variantName || '';
        const latestPricing = Array.isArray(v.pricing) && v.pricing.length > 0 ? v.pricing[0] : null;
        const barcode = currentEanOf(v);
        const displayLabel = [productLongDesc, variantName, product.sku, barcode]
          .filter((x: any) => x && String(x).trim().length > 0)
          .join(' · ');
        seedRows.push({
          ...v,
          barcode,
          id: v.id,
          variantName: variantName || productLongDesc || '(unnamed)',
          displayLabel,
          longDescription: productLongDesc,
          productSku: product.sku,
          productName: product.name,
          mrp: latestPricing ? parseFloat(latestPricing.mrp) : null,
          costPrice: latestPricing ? parseFloat(latestPricing.costPrice) : null,
          sellingPrice: latestPricing ? parseFloat(latestPricing.sellingPrice) : null,
        });
      } else {
        seedRows.push({
          id: it.variantId,
          variantName: '(saved)',
          displayLabel: 'Saved variant ···' + it.variantId.slice(-6),
          longDescription: '',
          productSku: null,
          productName: null,
          mrp: null,
          costPrice: null,
          sellingPrice: null,
        });
      }
    }

    // STEP 1: update variantList BEFORE touching the form value. Upsert by
    // id so a freshly-fetched enriched row OVERRIDES any earlier "(saved)"
    // stub for the same variant.
    if (seedRows.length > 0) {
      const seedIds = new Set(seedRows.map((r) => r.id));
      const preserved = this.variantList.filter((v) => !seedIds.has(v.id));
      this.variantList = [...preserved, ...seedRows];
    }

    // STEP 2: (re)set the form value AFTER variantList is current. ng-select
    // caches the selected-item OBJECTS at the moment the value is set — if we
    // set the ids while variantList still held stubs, the pills would keep
    // showing the stub labels even after variantList is upgraded (the value
    // didn't change, so ng-select never re-resolves). Force a re-resolve by
    // clearing then re-setting the control, so ng-select rebinds against the
    // now-enriched variantList and the pills show the real product names.
    if (ids.length) {
      const ctrl = this.offerForm.get('variantIds');
      ctrl?.setValue([]);
      ctrl?.setValue([...ids]);
    }
  }

  private makeFreeItemRow(variantId: string | null = null, quantity: number = 1): FormGroup {
    return this.fb.group({
      variantId: [variantId, Validators.required],
      quantity: [quantity, [Validators.required, Validators.min(1)]],
    });
  }

  addFreeItemRow() {
    this.freeItemsArray.push(this.makeFreeItemRow());
  }

  removeFreeItemRow(idx: number) {
    this.freeItemsArray.removeAt(idx);
  }

  getDateOnly(iso: string): string {
    return new Date(iso).toISOString().substring(0, 10);
  }

  /**
   * Coerce a form value to a finite number. Returns undefined for null,
   * empty string, or any value that fails Number() — keeping the cleanup
   * filter's drop-null-or-blank semantics intact for the DTO.
   */
  private toNumberOrUndefined(v: any): number | undefined {
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }

  onProductSelectFocus() {
    this.productInput$.next('');
  }

  loadStores() {
    this.offersService.getStores().subscribe({
      next: (resp: any) => {
        // /stores returns either an array or { data: [] } depending on endpoint
        // shape across services — handle both defensively.
        this.stores = Array.isArray(resp) ? resp : (resp?.data ?? []);
      },
      error: () => { this.stores = []; },
    });
  }

  setupProductSearch() {
    // Use the rich /products/search endpoint (same one GRN / PO / MBQ
    // use) so the dropdown matches on longDescription, SKU, and variant
    // barcode — not just variantName. Lets admins search "cadbury" and
    // get every Cadbury SKU regardless of variant name precision.
    this.productInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.loading = true)),
      switchMap((term) =>
        this.offersService.searchProductsForOffer(term).pipe(
          catchError(() => of({ data: [] })),
          tap(() => (this.loading = false)),
        ),
      ),
    ).subscribe((response: any) => {
      const products = response?.data || [];
      this.products = products;

      // Flatten products → variants and enrich each row with the parent
      // product's longDescription + sku so ng-select templates can show
      // both the variant-level identity and the catalog-level context.
      // Display label format: "{longDescription} — {variantName}  ·  SKU: {sku}"
      //
      // IMPORTANT: we MERGE new search results into the existing variantList
      // instead of replacing it wholesale. Replacing would wipe items that
      // were seeded by hydrateVariantIds / hydrateFreeItems (saved variants
      // needed to render pills on the edit form). We add only items whose id
      // is not already present so the seeded pills keep their rich labels.
      const searchResults = products.flatMap((product: any) => {
        return (product.variants || []).map((variant: any) => {
          const productLongDesc = product.longDescription || product.name || '';
          const variantName = variant.variantName || '';
          // Headline string used by bindLabel + the search filter. Keep
          // every searchable token in here so ng-select's built-in
          // search matches anything the admin might type.
          const barcode = currentEanOf(variant);
          const displayLabel = [productLongDesc, variantName, product.sku, barcode]
            .filter((x) => x && String(x).trim().length > 0)
            .join(' · ');

          // Pull the latest pricing row. Backend already orders
          // pricing by updatedAt DESC + isActive=true so pricing[0] is
          // the currently-effective price. Decimal columns serialize as
          // JSON strings so coerce with parseFloat. null when no active
          // pricing exists — template renders "—" in that case.
          const latestPricing = Array.isArray(variant.pricing) && variant.pricing.length > 0
            ? variant.pricing[0]
            : null;
          const mrp = latestPricing ? parseFloat(latestPricing.mrp) : null;
          const costPrice = latestPricing ? parseFloat(latestPricing.costPrice) : null;
          const sellingPrice = latestPricing ? parseFloat(latestPricing.sellingPrice) : null;

          return {
            ...variant,
            barcode,
            // Keep the legacy field name for any other consumer that
            // still reads variantName, but make sure it's never blank.
            variantName: variantName || productLongDesc || '(unnamed)',
            // New fields powering the richer ng-select templates.
            displayLabel,
            longDescription: productLongDesc,
            productSku: product.sku,
            productName: product.name,
            // Flattened pricing for the dropdown badges.
            mrp,
            costPrice,
            sellingPrice,
          };
        });
      });

      // Merge: keep seeded items (by id) that the search didn't return,
      // so already-selected variant pills stay rendered after each search.
      // Search hits go FIRST. With [typeahead] ng-select does not filter the
      // list itself, so whatever is at the top is what the admin sees — an
      // offer with 70+ saved variants used to push every hit below the fold
      // and look like "search does nothing". The pickers also set
      // hideSelected so already-chosen rows never crowd the dropdown.
      const resultIds = new Set(searchResults.map((r: any) => r.id));
      const preserved = this.variantList.filter((v) => !resultIds.has(v.id));
      this.variantList = [...searchResults, ...preserved];
    });
  }

  saveOffer() {
    if (!this.offerForm.valid) {
      this.offerForm.markAllAsTouched();
      return;
    }
    const raw = this.offerForm.value;
    const offerType: string = raw.offerType;

    // Build the payload. Stamp the role onto each freeItems row based on the
    // offer type so admins never have to pick it (and can't pick wrong).
    const usesMultiFree = this.multiFreeOfferTypes.has(offerType);
    // CHOICE = cashier picks (bill free item); everything else is given as a set.
    const role = offerType === 'BillRangeGetFreeItem' ? 'CHOICE' : 'SET';
    const freeItems = usesMultiFree
      ? (raw.freeItems || [])
          .filter((r: any) => !!r?.variantId)
          .map((r: any) => ({
            variantId: r.variantId,
            quantity: this.toNumberOrUndefined(r.quantity) ?? 1,
            role,
          }))
      : undefined;

    // Coerce all numeric form values from string → number. Prisma Decimal
    // columns serialize as JSON strings on the GET (e.g. "500.00"), which
    // patchValue then drops verbatim into the form control. On save those
    // strings would fail @IsNumber() in the DTO. The HTML input type=number
    // doesn't help — that's only display formatting, not value coercion.
    const numericFields = [
      'priceOffAmount',
      'percentOff',
      'billAmountMin',
      'billAmountMax',
      'buyQuantity',
      'freeQuantity',
      'priority',
      'freeChoiceCount',
    ];
    const coerced: Record<string, any> = {};
    for (const f of numericFields) {
      const v = this.toNumberOrUndefined(raw[f]);
      if (v !== undefined) coerced[f] = v;
    }

    const visible = this.isFieldVisible;
    const slabs = (raw.slabs || [])
      .map((s: any) => ({
        minQty: this.toNumberOrUndefined(s?.minQty),
        percentOff: this.toNumberOrUndefined(s?.percentOff),
        priceOffAmount: this.toNumberOrUndefined(s?.priceOffAmount),
      }))
      .filter((s: any) => s.minQty && (s.percentOff || s.priceOffAmount))
      .map((s: any) => ({
        minQty: s.minQty,
        ...(s.percentOff ? { percentOff: s.percentOff } : { priceOffAmount: s.priceOffAmount }),
      }));
    const { slabs: _slabs, ...rawWithoutSlabs } = raw;

    const formValue: any = {
      ...rawWithoutSlabs,
      ...coerced, // numeric coercions override raw string values
      startDate: new Date(raw.startDate).toISOString(),
      endDate: new Date(raw.endDate).toISOString(),
      // Daily window only means something for a time-boxed type; "HH:mm"
      // goes to the backend as-is.
      startTime: visible('startTime') ? raw.startTime : undefined,
      endTime: visible('endTime') ? raw.endTime : undefined,
      progressiveDiscount: offerType === 'ProgressiveDiscount' ? slabs : undefined,
      // Drop the legacy single-variant FREE field for multi-free offers
      // so the backend doesn't get conflicting state.
      freeItemVariantId: usesMultiFree ? null : raw.freeItemVariantId,
      freeItems,
    };

    // Strip values that would fail DTO validation. class-validator's
    // @IsOptional() only skips `undefined`, NOT `null` or `''`, so any
    // empty form control (e.g. blank startTime / cleared freeItemVariantId)
    // would 400 the PATCH/POST. Rules:
    //   - drop null + empty-string scalars (they were never user-supplied)
    //   - keep empty arrays (admin clearing storeIds is a meaningful edit:
    //     "remove all store scoping — apply to every store")
    //   - keep freeItems array even when empty (signals "wipe rows" on edit)
    //   - keep false booleans + 0 numbers (legitimate values)
    const cleanedFormValue: any = Object.fromEntries(
      Object.entries(formValue).filter(([k, v]) => {
        if (k === 'freeItems') return Array.isArray(v);
        if (Array.isArray(v)) return true;
        return v !== null && v !== undefined && v !== '';
      }),
    );

    this.saving = true;
    const req$ = this.data?.id
      ? this.offersService.updateOffer(this.data.id, cleanedFormValue)
      : this.offersService.addOffer(cleanedFormValue as Offer);
    const successMsg = this.data?.id ? 'Offer updated successfully' : 'Offer saved successfully';
    const errorMsg = this.data?.id ? 'Failed to update offer' : 'Failed to save offer';

    req$.subscribe({
      next: (result) => {
        this.saving = false;
        this.toastr.success(successMsg);
        // Two close paths depending on context:
        //   modal mode (Edit) → resolve parent's modalRef.result promise
        //     which triggers loadOffers() in offers.component
        //   page mode (Add via /create-offer route) → navigate back to
        //     the list, which re-runs its loadOffers() in ngOnInit
        if (this.activeModal) {
          this.activeModal.close(result);
        } else {
          this.router.navigate(['/offers-list']);
        }
      },
      error: () => {
        this.saving = false;
        this.toastr.error(errorMsg);
        // Form stays open on error so the admin can fix input and retry
        // without losing what they typed. Same in both modal + page mode.
      },
    });
  }

  /** Cancel button / X close. Dismisses the modal (Edit flow) or
   *  navigates back to the list (standalone Add page). */
  cancel() {
    if (this.activeModal) {
      this.activeModal.dismiss('cancel');
    } else {
      this.router.navigate(['/offers-list']);
    }
  }

  fieldVisibilityMap: Record<string, string[]> = {
    PriceOffOnProduct: ['priceOffAmount', 'variantIds', 'exclusionVariantIds'],
    PercentOffOnMRP: ['percentOff', 'variantIds', 'exclusionVariantIds'],
    BuyXGetYFree: ['buyQuantity', 'freeQuantity', 'percentOff', 'priceOffAmount', 'variantIds'],
    BuyXYGetZSpecialPrice: ['priceOffAmount', 'buyQuantity', 'variantIds', 'freeItems'],
    BuyXYGetZFree: ['freeItems', 'variantIds'],
    BuyXYGetDiscountOnX: ['percentOff', 'variantIds'],
    BuyXYGetAmountDiscountOnX: ['priceOffAmount', 'variantIds'],
    BillRangeGetPercentOff: ['billAmountMin', 'billAmountMax', 'percentOff'],
    BillRangeGetAmountOff: ['billAmountMin', 'billAmountMax', 'priceOffAmount'],
    BillRangeGetFreeItem: ['billAmountMin', 'billAmountMax', 'freeItems', 'freeChoiceCount'],
    BillRangeGetFreeSet: ['billAmountMin', 'billAmountMax', 'freeItems'],
    BillRangeGetScratchCard: ['billAmountMin', 'billAmountMax'],
    CategoryPercentDiscount: ['percentOff', 'categoryIds', 'exclusionVariantIds'],
    DepartmentPercentDiscount: ['percentOff', 'departmentIds', 'exclusionVariantIds'],
    BrandPercentDiscount: ['percentOff', 'brandIds', 'exclusionVariantIds'],
    AllProductsDiscount: ['percentOff', 'priceOffAmount', 'exclusionVariantIds'],
    HappyHourDiscount: ['startTime', 'endTime', 'percentOff', 'priceOffAmount', 'variantIds', 'exclusionVariantIds'],
    ProgressiveDiscount: ['progressiveDiscount', 'variantIds', 'exclusionVariantIds'],
    ExclusionBasedDiscount: ['percentOff', 'priceOffAmount', 'exclusionVariantIds']
  };

  // storeIds is universally available — every offer can be scoped to one or
  // more stores. Empty selection = applies to all stores (backend default).
  get isFieldVisible() {
    const offerType = this.offerForm.get('offerType')?.value;
    const visibleFields = this.fieldVisibilityMap[offerType] || [];
    return (field: string) => {
      if (field === 'storeIds') return true;
      return visibleFields.includes(field);
    };
  }
}
